import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $generateNodesFromDOM, $generateHtmlFromNodes } from "@lexical/html";
import { $getRoot } from "lexical";
import { useEffect } from "react";
import { $patchStyleText } from "@lexical/selection";
import { $isTableNode, TableNode } from "@lexical/table";
import TurndownService from 'turndown';

const turndownPluginGfm = require('@joplin/turndown-plugin-gfm');

function removeBoldString(str: string) {
  const re = new RegExp('(<b([^>]*)">)|(<b>)|(</b>)', "g");
  return str.replace(re, "");
}

type Props = {
  state: string;
  onChange?: (html: string, markdown: string) => void;
};

const RestoreEditorStatePlugin: React.FC<Props> = ({ state, onChange }) => {
  const [editor] = useLexicalComposerContext();

  const turndownService = new TurndownService();
  turndownService.use(turndownPluginGfm.gfm);

  useEffect(() => {
    if ((state ?? "").trim().length === 0) {
      editor.update(() => {
        const selection = $getRoot().select();
        $patchStyleText(selection, { color: "#000", "font-size": "16px" });
      });
    } else {
      editor.update(() => {
        const selection = $getRoot().select();

        // remove previos nodes and clear the editor
        for (let node of selection.getNodes()) {
          node.remove();
        }
        $getRoot().clear();

        //create new nodes and insert into root selection
        const parser = new DOMParser();
        const dom = parser.parseFromString(state, "text/html");
        const tableElements = dom.getElementsByTagName("table");

        // tables align (center, right) are lost after parsing DOM into lexical nodes, so we memorize
        // them in `tablesFormats` array in order to restore the alignments after converting DOM into
        // lexical nodes
        let tablesFormats = [];
        //@ts-ignore
        for (let t of tableElements) {
          if (t.className.includes("tableAlignmentCenter")) {
            tablesFormats.push("center");
          } else if (t.className.includes("tableAlignmentRight")) {
            tablesFormats.push("right");
          }
        }

        const nodes = $generateNodesFromDOM(editor, dom);

        // put all table nodes into a separate array in order to be able to restore their lost aligns
        let tableNodes: TableNode[] = [];
        for (let n of nodes) {
          if ($isTableNode(n)) {
            tableNodes.push(n);
          }
        }
        // here table aligns(or formats) are restored before being inserted into root selection
        //@ts-ignore
        for (let [index, tableNode] of tableNodes.entries()) {
          const domFormat = tablesFormats?.[index];
          if (domFormat) {
            tableNode.setFormat(domFormat);
          }
        }

        selection.insertNodes(nodes);
      });
    }
  }, [state]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const htmlString = removeBoldString(
          $generateHtmlFromNodes(editor, null)
        );

        const markdownString = turndownService.turndown(htmlString);

        onChange?.(htmlString, markdownString);
      });
    });
  }, [editor]);

  return null;
};

export default RestoreEditorStatePlugin;
